import React from 'react';
import {
  Button,
  Box,
  Typography,
  TextField,
  IconButton,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  Menu,
} from '@mui/material';
import {
  Close as CloseIcon,
  ChevronLeft as ChevronLeftIcon,
  Check as CheckIcon,
  CloudUpload as UploadIcon,
  Collections as LibraryIcon,
} from '@mui/icons-material';
import { CtaButton } from '../../../../../types';
import { ImageLibraryModal } from '../../../ImageLibraryModal';
import { ImageMetadata, ImageService } from '../../../../../services/imageService';
import { RightDrawer, ConfirmDialog, ImageUploadField } from '../../../common';

interface CtaButtonEditDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (ctaButton: CtaButton) => void;
  onDelete?: (id: string) => void;
  ctaButton: CtaButton | null;
  siteId?: string;
  cachedImages?: ImageMetadata[];
  preloaded?: boolean;
  editState?: any;
  onPendingCtaImageFile?: (buttonId: string, file: File | null) => void;
  onPendingCtaHoverImageFile?: (buttonId: string, file: File | null) => void;
}

export const CtaButtonEditDialog: React.FC<CtaButtonEditDialogProps> = ({
  open,
  onClose,
  onSave,
  onDelete,
  ctaButton,
  siteId,
  cachedImages = [],
  preloaded = false,
  editState,
  onPendingCtaImageFile,
  onPendingCtaHoverImageFile,
}) => {
  const [name, setName] = React.useState('');
  const [image, setImage] = React.useState('');
  const [imagePreview, setImagePreview] = React.useState('');
  const [hoverImage, setHoverImage] = React.useState('');
  const [hoverImagePreview, setHoverImagePreview] = React.useState('');
  const [displayMode, setDisplayMode] = React.useState<'fixed' | 'embedded'>('fixed');
  const [bottomPercent, setBottomPercent] = React.useState<number | undefined>(5);
  const [bottomPercentInput, setBottomPercentInput] = React.useState<string>('5');
  const [leftPosition, setLeftPosition] = React.useState<'center' | 'left' | 'right' | 'custom'>('center');
  const [leftPercentCustom, setLeftPercentCustom] = React.useState<number>(0);
  const [leftPercentCustomInput, setLeftPercentCustomInput] = React.useState<string>('0');
  const [widthPercent, setWidthPercent] = React.useState<number>(80);
  const [widthPercentInput, setWidthPercentInput] = React.useState<string>('80');
  const [linkUrl, setLinkUrl] = React.useState('');
  const [openInNewTab, setOpenInNewTab] = React.useState(false);
  const [animationType, setAnimationType] = React.useState<'none' | 'bounce' | 'float' | 'pulse'>('none');
  const [imageLibraryModalOpen, setImageLibraryModalOpen] = React.useState(false);
  const [hoverImageLibraryModalOpen, setHoverImageLibraryModalOpen] = React.useState(false);
  const [imageMenuAnchor, setImageMenuAnchor] = React.useState<HTMLElement | null>(null);
  const [hoverImageMenuAnchor, setHoverImageMenuAnchor] = React.useState<HTMLElement | null>(null);
  const imageFileInputRef = React.useRef<HTMLInputElement>(null);
  const hoverImageFileInputRef = React.useRef<HTMLInputElement>(null);
  const [libraryImages, setLibraryImages] = React.useState<ImageMetadata[]>([]);
  const [libraryVideos, setLibraryVideos] = React.useState<ImageMetadata[]>([]);
  const [libraryImagesLoading, setLibraryImagesLoading] = React.useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);
  const [confirmCloseOpen, setConfirmCloseOpen] = React.useState(false);
  const currentButtonId = ctaButton?.id ?? null;
  const initialValuesRef = React.useRef<{
    name: string;
    image: string;
    hoverImage: string;
    displayMode: 'fixed' | 'embedded';
    bottomPercent: number | undefined;
    leftPosition: 'center' | 'left' | 'right' | 'custom';
    leftPercentCustom: number;
    widthPercent: number;
    linkUrl: string;
    openInNewTab: boolean;
    animationType: 'none' | 'bounce' | 'float' | 'pulse';
  } | null>(null);

  // 初期化
  React.useEffect(() => {
    if (open && ctaButton) {
      const initialBottomPercent = ctaButton.bottomPercent;
      const initialLeftPercentCustom = ctaButton.leftPercentCustom ?? 0;
      const initialWidthPercent = ctaButton.widthPercent ?? 80;
      const initialValues = {
        name: ctaButton.name,
        image: ctaButton.image || '',
        hoverImage: ctaButton.hoverImage || '',
        displayMode: ctaButton.displayMode || 'fixed',
        bottomPercent: initialBottomPercent,
        leftPosition: ctaButton.leftPosition || 'center',
        leftPercentCustom: initialLeftPercentCustom,
        widthPercent: initialWidthPercent,
        linkUrl: ctaButton.linkUrl || '',
        openInNewTab: ctaButton.openInNewTab || false,
        animationType: ctaButton.animationType || 'none',
      };
      initialValuesRef.current = initialValues;
      setName(ctaButton.name);
      setImage(ctaButton.image || '');
      setImagePreview(ctaButton.image || '');
      setHoverImage(ctaButton.hoverImage || '');
      setHoverImagePreview(ctaButton.hoverImage || '');
      setDisplayMode(ctaButton.displayMode || 'fixed');
      setBottomPercent(initialBottomPercent);
      setBottomPercentInput(initialBottomPercent !== undefined ? String(initialBottomPercent) : '');
      setLeftPosition(ctaButton.leftPosition || 'center');
      setLeftPercentCustom(initialLeftPercentCustom);
      setLeftPercentCustomInput(String(initialLeftPercentCustom));
      setWidthPercent(initialWidthPercent);
      setWidthPercentInput(String(initialWidthPercent));
      setLinkUrl(ctaButton.linkUrl || '');
      setOpenInNewTab(ctaButton.openInNewTab || false);
      setAnimationType(ctaButton.animationType || 'none');
    } else if (open && !ctaButton) {
      // 新規作成の場合
      const initialValues = {
        name: '',
        image: '',
        hoverImage: '',
        displayMode: 'fixed' as const,
        bottomPercent: 5,
        leftPosition: 'center' as const,
        leftPercentCustom: 0,
        widthPercent: 80,
        linkUrl: '',
        openInNewTab: false,
        animationType: 'none' as const,
      };
      initialValuesRef.current = initialValues;
      setName('');
      setImage('');
      setImagePreview('');
      setHoverImage('');
      setHoverImagePreview('');
      setDisplayMode('fixed');
      setBottomPercent(5);
      setBottomPercentInput('5');
      setLeftPosition('center');
      setLeftPercentCustom(0);
      setLeftPercentCustomInput('0');
      setWidthPercent(80);
      setWidthPercentInput('80');
      setLinkUrl('');
      setOpenInNewTab(false);
      setAnimationType('none');
    } else if (!open) {
      initialValuesRef.current = null;
    }
  }, [open, ctaButton]);

  const handleSave = () => {
    if (!name.trim()) {
      alert('ボタン名を入力してください');
      return;
    }

    const savedButton: CtaButton = {
      id: ctaButton?.id || `cta-${Date.now()}`,
      name: name.trim(),
      html: '',
      css: '',
      image,
      hoverImage,
      displayMode,
      bottomPercent,
      leftPosition,
      leftPercentCustom,
      widthPercent,
      linkUrl: linkUrl.trim(),
      openInNewTab,
      animationType
    };

    onSave(savedButton);
    onClose();
  };

  const hasChanges = (): boolean => {
    if (!initialValuesRef.current) return false;
    const initial = initialValuesRef.current;
    return (
      name.trim() !== initial.name.trim() ||
      image !== initial.image ||
      hoverImage !== initial.hoverImage ||
      displayMode !== initial.displayMode ||
      bottomPercent !== initial.bottomPercent ||
      leftPosition !== initial.leftPosition ||
      leftPercentCustom !== initial.leftPercentCustom ||
      widthPercent !== initial.widthPercent ||
      linkUrl.trim() !== initial.linkUrl.trim() ||
      openInNewTab !== initial.openInNewTab ||
      animationType !== initial.animationType
    );
  };

  const handleCloseRequest = () => {
    if (hasChanges()) {
      setConfirmCloseOpen(true);
    } else {
      onClose();
    }
  };

  const handleCloseConfirm = () => {
    if (siteId === 'create' && currentButtonId) {
      onPendingCtaImageFile?.(currentButtonId, null);
      onPendingCtaHoverImageFile?.(currentButtonId, null);
    }
    if (initialValuesRef.current) {
      const initial = initialValuesRef.current;
      setName(initial.name);
      setImage(initial.image);
      setImagePreview(initial.image);
      setHoverImage(initial.hoverImage);
      setHoverImagePreview(initial.hoverImage);
      setDisplayMode(initial.displayMode);
      setBottomPercent(initial.bottomPercent);
      setBottomPercentInput(initial.bottomPercent !== undefined ? String(initial.bottomPercent) : '');
      setLeftPosition(initial.leftPosition);
      setLeftPercentCustom(initial.leftPercentCustom);
      setLeftPercentCustomInput(String(initial.leftPercentCustom));
      setWidthPercent(initial.widthPercent);
      setWidthPercentInput(String(initial.widthPercent));
      setLinkUrl(initial.linkUrl);
      setOpenInNewTab(initial.openInNewTab);
      setAnimationType(initial.animationType);
    }
    setConfirmCloseOpen(false);
    onClose();
  };

  const handleCloseCancel = () => {
    setConfirmCloseOpen(false);
  };

  // ライブラリ画像を読み込む
  React.useEffect(() => {
    if (!open || !siteId || siteId === 'create') return;

    if (preloaded && cachedImages.length > 0) {
      const images = cachedImages.filter(img => !img.isVideo);
      const videos = cachedImages.filter(img => img.isVideo);
      setLibraryImages(images);
      setLibraryVideos(videos);
      setLibraryImagesLoading(false);
      return;
    }

    let cancelled = false;
    setLibraryImagesLoading(true);
    (async () => {
      try {
        const allMedia = await ImageService.getSiteImageLibrary(siteId);
        if (!cancelled) {
          const images = allMedia.filter(img => !img.isVideo);
          const videos = allMedia.filter(img => img.isVideo);
          setLibraryImages(images);
          setLibraryVideos(videos);
        }
      } catch (error) {
        console.error('画像の読み込みに失敗しました:', error);
      } finally {
        if (!cancelled) setLibraryImagesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, siteId, cachedImages, preloaded]);

  // 使用中の画像URLを集約
  const getUsedImageUrls = (): Set<string> => {
    const usedUrls = new Set<string>();
    
    if (!editState) return usedUrls;
    
    // ページの背景画像
    if (editState.pages && Array.isArray(editState.pages)) {
      editState.pages.forEach((page: any) => {
        if (page.backgroundImage) {
          usedUrls.add(page.backgroundImage);
        }
      });
    }
    
    // サイト設定の背景画像
    if (editState.settings?.backgroundImage) {
      usedUrls.add(editState.settings.backgroundImage);
    }
    
    // CTAボタンの画像
    if (editState.settings?.ctaButtons && Array.isArray(editState.settings.ctaButtons)) {
      editState.settings.ctaButtons.forEach((btn: any) => {
        if (btn.image) usedUrls.add(btn.image);
        if (btn.hoverImage) usedUrls.add(btn.hoverImage);
      });
    }
    
    // 現在編集中のボタンの画像は除外（自分自身）
    if (ctaButton) {
      if (ctaButton.image) usedUrls.delete(ctaButton.image);
      if (ctaButton.hoverImage) usedUrls.delete(ctaButton.hoverImage);
    }
    
    return usedUrls;
  };

  // デバイスから画像をアップロード（新規時は一時保存のみ）
  const handleImageFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!ImageService.isSupportedImageType(file) || !ImageService.isValidFileSize(file, 100)) {
      alert('画像ファイル（JPEG/PNG/WebP/GIF、最大100MB）を選択してください');
      return;
    }
    if (siteId === 'create' && onPendingCtaImageFile && currentButtonId) {
      onPendingCtaImageFile(currentButtonId, file);
      const url = URL.createObjectURL(file);
      setImage(url);
      setImagePreview(url);
      if (imageFileInputRef.current) imageFileInputRef.current.value = '';
      return;
    }
    if (!siteId || siteId === 'create') return;
    try {
      let fileExtension = file.type === 'image/gif' ? 'gif' : 'webp';
      const originalName = file.name.replace(/\.[^/.]+$/, '');
      const originalFileName = `${originalName}.${fileExtension}`;
      const isVideo = false;
      const fileName = await ImageService.getAvailableFileName(originalFileName, siteId, isVideo);
      const uploadedUrl = await ImageService.uploadMedia(file, siteId, undefined, {
        fileName,
        overwrite: false,
        autoNumber: false,
      });
      setImage(uploadedUrl);
      setImagePreview(uploadedUrl);
    } catch (error) {
      console.error('画像のアップロードに失敗しました:', error);
      alert('画像のアップロードに失敗しました');
    } finally {
      if (imageFileInputRef.current) imageFileInputRef.current.value = '';
    }
  };

  const handleHoverImageFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!ImageService.isSupportedImageType(file) || !ImageService.isValidFileSize(file, 100)) {
      alert('画像ファイル（JPEG/PNG/WebP/GIF、最大100MB）を選択してください');
      return;
    }
    if (siteId === 'create' && onPendingCtaHoverImageFile && currentButtonId) {
      onPendingCtaHoverImageFile(currentButtonId, file);
      const url = URL.createObjectURL(file);
      setHoverImage(url);
      setHoverImagePreview(url);
      if (hoverImageFileInputRef.current) hoverImageFileInputRef.current.value = '';
      return;
    }
    if (!siteId || siteId === 'create') return;
    try {
      let fileExtension = file.type === 'image/gif' ? 'gif' : 'webp';
      const originalName = file.name.replace(/\.[^/.]+$/, '');
      const originalFileName = `${originalName}.${fileExtension}`;
      const isVideo = false;
      const fileName = await ImageService.getAvailableFileName(originalFileName, siteId, isVideo);
      const uploadedUrl = await ImageService.uploadMedia(file, siteId, undefined, {
        fileName,
        overwrite: false,
        autoNumber: false,
      });
      setHoverImage(uploadedUrl);
      setHoverImagePreview(uploadedUrl);
    } catch (error) {
      console.error('画像のアップロードに失敗しました:', error);
      alert('画像のアップロードに失敗しました');
    } finally {
      if (hoverImageFileInputRef.current) {
        hoverImageFileInputRef.current.value = '';
      }
    }
  };

  const handleImageSelected = (imageUrl: string) => {
    setImage(imageUrl);
    setImagePreview(imageUrl);
  };

  const handleHoverImageSelected = (imageUrl: string) => {
    setHoverImage(imageUrl);
    setHoverImagePreview(imageUrl);
  };

  return (
    <>
      {/* 画像選択ダイアログ ※保存済みサイトのみ */}
      {siteId && siteId !== 'create' && (
        <>
          <ImageLibraryModal
            open={imageLibraryModalOpen}
            onClose={() => setImageLibraryModalOpen(false)}
            onSelect={handleImageSelected}
            images={libraryImages}
            videos={libraryVideos}
            title="CTAボタン画像を選択"
            usedImageUrls={getUsedImageUrls()}
            loading={libraryImagesLoading}
          />
          <ImageLibraryModal
            open={hoverImageLibraryModalOpen}
            onClose={() => setHoverImageLibraryModalOpen(false)}
            onSelect={handleHoverImageSelected}
            images={libraryImages}
            videos={libraryVideos}
            title="CTAボタンホバー画像を選択"
            usedImageUrls={getUsedImageUrls()}
            loading={libraryImagesLoading}
          />
        </>
      )}
      
      {/* 画像選択メニュー（新規時も表示） */}
      {siteId && (
        <>
          <Menu
            anchorEl={imageMenuAnchor}
            open={Boolean(imageMenuAnchor)}
            onClose={() => setImageMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            PaperProps={{ sx: { borderRadius: '8px', boxShadow: '0px 4px 12px rgba(0,0,0,0.15)', mt: 0.5, minWidth: 180 } }}
          >
            <MenuItem onClick={() => { setImageMenuAnchor(null); imageFileInputRef.current?.click(); }} sx={{ py: 1.5, px: 2 }}>
              <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center' }}><UploadIcon sx={{ fontSize: '18px', color: '#616161' }} /></Box>
              デバイスから選択
            </MenuItem>
            <MenuItem onClick={() => { setImageMenuAnchor(null); if (siteId !== 'create') setImageLibraryModalOpen(true); }} sx={{ py: 1.5, px: 2 }}>
              <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center' }}><LibraryIcon sx={{ fontSize: '18px', color: '#616161' }} /></Box>
              ライブラリから選択
            </MenuItem>
          </Menu>
          <Menu
            anchorEl={hoverImageMenuAnchor}
            open={Boolean(hoverImageMenuAnchor)}
            onClose={() => setHoverImageMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            PaperProps={{ sx: { borderRadius: '8px', boxShadow: '0px 4px 12px rgba(0,0,0,0.15)', mt: 0.5, minWidth: 180 } }}
          >
            <MenuItem onClick={() => { setHoverImageMenuAnchor(null); hoverImageFileInputRef.current?.click(); }} sx={{ py: 1.5, px: 2 }}>
              <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center' }}><UploadIcon sx={{ fontSize: '18px', color: '#616161' }} /></Box>
              デバイスから選択
            </MenuItem>
            <MenuItem onClick={() => { setHoverImageMenuAnchor(null); if (siteId !== 'create') setHoverImageLibraryModalOpen(true); }} sx={{ py: 1.5, px: 2 }}>
              <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center' }}><LibraryIcon sx={{ fontSize: '18px', color: '#616161' }} /></Box>
              ライブラリから選択
            </MenuItem>
          </Menu>
          <input ref={imageFileInputRef} type="file" accept="image/jpeg,image/jpg,image/png,image/webp,image/gif" onChange={handleImageFileSelect} style={{ display: 'none' }} />
          <input ref={hoverImageFileInputRef} type="file" accept="image/jpeg,image/jpg,image/png,image/webp,image/gif" onChange={handleHoverImageFileSelect} style={{ display: 'none' }} />
        </>
      )}

       <RightDrawer
        open={open}
        onClose={handleCloseRequest}
         width={{ xs: '100%', sm: '850px' }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: '#F7F7F7' }}>
          {/* ヘッダー */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                size="small"
                onClick={handleCloseRequest}
                sx={{ 
                  padding: '8px',
                }}
              >
                <ChevronLeftIcon />
              </IconButton>
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '18px' }}>
                CTAボタン 新規作成 / 編集
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={handleCloseRequest}
              sx={{
                padding: '8px',
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* コンテンツ */}
          <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex' }}>
          {/* 左側：設定エリア（スクロール可能） */}
          <Box 
            sx={{ 
              width: '500px', 
              display: 'flex', 
              flexDirection: 'column', 
              gap: 2,
              overflowY: 'auto',
              p: 3,
              '&::-webkit-scrollbar': {
                display: 'none',
              },
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
            }}
          >
              {/* 基本情報 */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '16px', mb: 1 }}>
                  基本情報
                </Typography>
                
                {/* ボタン名 */}
                <TextField
                  fullWidth
                  label="ボタン名"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="ボタン名（例：メインCTAボタン）"
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      backgroundColor: '#FFFFFF',
                      '& fieldset': {
                        borderColor: '#D0D0D0',
                      },
                    },
                  }}
                />
                
                {/* 遷移先URL */}
                <TextField
                  fullWidth
                  label="遷移先URL"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  placeholder="遷移先URL（例：line.me、/contact）"
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      backgroundColor: '#FFFFFF',
                      '& fieldset': {
                        borderColor: '#D0D0D0',
                      },
                    },
                  }}
                />
                
                {/* 新しいタブで開く */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    onClick={() => setOpenInNewTab(!openInNewTab)}
                    sx={{
                      position: 'relative',
                      width: '48px',
                      height: '24px',
                      backgroundColor: openInNewTab ? '#1B76D2' : '#E0E0E0',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      transition: 'background-color 0.3s',
                      display: 'flex',
                      alignItems: 'center',
                      padding: '2px',
                    }}
                  >
                    <Box
                      sx={{
                        width: '20px',
                        height: '20px',
                        backgroundColor: '#FFFFFF',
                        borderRadius: '50%',
                        transition: 'transform 0.3s',
                        transform: openInNewTab ? 'translateX(24px)' : 'translateX(0px)',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                      }}
                    />
                  </Box>
                  <Typography variant="body2" sx={{ fontSize: '14px', color: '#000000' }}>
                    新しいタブで開く : {openInNewTab ? 'ON' : 'OFF'}
                  </Typography>
                </Box>
              </Box>

              {/* 画像 */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <ImageUploadField
                  label="画像"
                  helperText="JPEG, PNG, WebP, GIF (最大100MB)"
                  value={imagePreview}
                  onClick={(e) => {
                    if (siteId) {
                      setImageMenuAnchor(e.currentTarget);
                    }
                  }}
                  onDelete={() => {
                          if (siteId === 'create' && currentButtonId) {
                            onPendingCtaImageFile?.(currentButtonId, null);
                          }
                          setImage('');
                          setImagePreview('');
                        }}
                  alt="CTAボタン画像"
                />
              </Box>

              {/* ホバー画像 */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <ImageUploadField
                  label="ホバー画像"
                  helperText="JPEG, PNG, WebP, GIF (最大100MB)"
                  value={hoverImagePreview}
                  onClick={(e) => {
                    if (siteId) {
                      setHoverImageMenuAnchor(e.currentTarget);
                    }
                  }}
                  onDelete={() => {
                          if (siteId === 'create' && currentButtonId) {
                            onPendingCtaHoverImageFile?.(currentButtonId, null);
                          }
                          setHoverImage('');
                          setHoverImagePreview('');
                        }}
                  alt="ホバー画像"
                />
              </Box>

              {/* 表示設定 */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '16px' }}>
                  表示設定
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '12px', color: '#666666', mb: 1 }}>
                  埋め込みはモーダルより下に配置されます
                </Typography>
                <FormControl fullWidth>
                  <Select
                    value={displayMode}
                    onChange={(e) => setDisplayMode(e.target.value as 'fixed' | 'embedded')}
                    sx={{
                      borderRadius: '8px',
                      backgroundColor: '#FFFFFF',
                      '& fieldset': {
                        borderColor: '#D0D0D0',
                      },
                    }}
                  >
                    <MenuItem value="fixed">固定表示 (position: fixed)</MenuItem>
                    <MenuItem value="embedded">埋め込み式（HTML/CSS内に配置）</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {/* 位置 */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '16px' }}>
                  位置
                </Typography>
                
                {/* 上下位置（下から） */}
                <TextField
                  fullWidth
                  label="上下位置（下から）"
                  value={bottomPercentInput}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '') {
                      setBottomPercentInput('');
                      setBottomPercent(0);
                      return;
                    }
                    const numValue = Number(value);
                    if (!isNaN(numValue) && numValue >= 0 && numValue <= 100) {
                      setBottomPercentInput(value);
                      setBottomPercent(numValue);
                    }
                  }}
                  type="number"
                  inputProps={{ min: 0, max: 100 }}
                  placeholder="5"
                  variant="outlined"
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      backgroundColor: '#FFFFFF',
                      '& fieldset': {
                        borderColor: '#D0D0D0',
                      },
                      '& input[type=number]': {
                        MozAppearance: 'textfield',
                        '&::-webkit-inner-spin-button': {
                          opacity: 1,
                          WebkitAppearance: 'inner-spin-button',
                        },
                        '&::-webkit-outer-spin-button': {
                          opacity: 1,
                        },
                      },
                    },
                  }}
                />

                {/* 左右位置 */}
                <FormControl fullWidth>
                  <InputLabel id="left-position-label">左右位置</InputLabel>
                  <Select
                    labelId="left-position-label"
                    label="左右位置"
                    value={leftPosition}
                    onChange={(e) => setLeftPosition(e.target.value as 'center' | 'left' | 'right' | 'custom')}
                    sx={{
                      borderRadius: '8px',
                      backgroundColor: '#FFFFFF',
                      '& fieldset': {
                        borderColor: '#D0D0D0',
                      },
                    }}
                  >
                    <MenuItem value="center">中央</MenuItem>
                    <MenuItem value="left">左端</MenuItem>
                    <MenuItem value="right">右端</MenuItem>
                    <MenuItem value="custom">カスタム</MenuItem>
                  </Select>
                </FormControl>

                {/* カスタム位置の入力欄 */}
                {leftPosition === 'custom' && (
                  <TextField
                    fullWidth
                    label="カスタム位置"
                    value={leftPercentCustomInput}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '') {
                        setLeftPercentCustomInput('');
                        setLeftPercentCustom(0);
                        return;
                      }
                      const numValue = Number(value);
                      if (!isNaN(numValue) && numValue >= 0 && numValue <= 100) {
                        setLeftPercentCustomInput(value);
                        setLeftPercentCustom(numValue);
                      }
                    }}
                    type="number"
                    inputProps={{ min: 0, max: 100 }}
                    placeholder="0"
                    variant="outlined"
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        backgroundColor: '#FFFFFF',
                        '& fieldset': {
                          borderColor: '#D0D0D0',
                        },
                        '& input[type=number]': {
                          MozAppearance: 'textfield',
                          '&::-webkit-inner-spin-button': {
                            opacity: 1,
                            WebkitAppearance: 'inner-spin-button',
                          },
                          '&::-webkit-outer-spin-button': {
                            opacity: 1,
                          },
                        },
                      },
                    }}
                  />
                )}
              </Box>

              {/* 横幅 */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '16px' }}>
                  横幅
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '12px', color: '#666666', mb: 1 }}>
                  画面幅の0~100%
                </Typography>
                <TextField
                  value={widthPercentInput}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '') {
                      setWidthPercentInput('');
                      setWidthPercent(0);
                      return;
                    }
                    const numValue = Number(value);
                    if (!isNaN(numValue) && numValue >= 0 && numValue <= 100) {
                      setWidthPercentInput(value);
                      setWidthPercent(numValue);
                    }
                  }}
                  type="number"
                  inputProps={{ min: 0, max: 100 }}
                  placeholder="100"
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      backgroundColor: '#FFFFFF',
                      '& fieldset': {
                        borderColor: '#D0D0D0',
                      },
                      '& input[type=number]': {
                        MozAppearance: 'textfield',
                        '&::-webkit-inner-spin-button': {
                          opacity: 1,
                          WebkitAppearance: 'inner-spin-button',
                        },
                        '&::-webkit-outer-spin-button': {
                          opacity: 1,
                        },
                      },
                    },
                  }}
                />
              </Box>

              {/* アニメーション */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 6 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '16px' }}>
                  アニメーション
                </Typography>
                <FormControl fullWidth>
                  <Select
                    value={animationType}
                    onChange={(e) => setAnimationType(e.target.value as 'none' | 'bounce' | 'float' | 'pulse')}
                    sx={{
                      borderRadius: '8px',
                      backgroundColor: '#FFFFFF',
                      '& fieldset': {
                        borderColor: '#D0D0D0',
                      },
                    }}
                  >
                    <MenuItem value="none">なし</MenuItem>
                    <MenuItem value="bounce">バウンス（上下に跳ねる）</MenuItem>
                    <MenuItem value="float">フロート（ゆっくり上下）</MenuItem>
                    <MenuItem value="pulse">パルス（拡大縮小）</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>

          {/* 右側：プレビューエリアとボタン */}
          <Box 
            sx={{ 
              width: '350px',
              display: 'flex', 
              flexDirection: 'column',
              backgroundColor: '#F7F7F7'
            }}
          >
            {/* プレビューエリア */}
            <Box 
              sx={{ 
                flex: 1,
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'flex-start',
                p: 2,
                minHeight: 0,
                pt: 3,
              }}
            >
              <Box
                sx={{
                  width: '280px',
                  height: '500px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '20px',
                  padding: '16px',
                  backgroundColor: '#f5f5f5',
                  position: 'relative',
                  overflow: 'hidden',
                  backgroundImage: editState?.pages?.[0]?.backgroundImage 
                    ? `url(${editState.pages[0].backgroundImage})` 
                    : 'none',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat'
                }}
              >
                {/* プレビュー用のCTAボタン */}
                {imagePreview && (() => {
                  // 位置計算
                  let leftStyle: string;
                  let transformStyle: string;
                  
                  switch (leftPosition) {
                    case 'center':
                      leftStyle = '50%';
                      transformStyle = 'translateX(-50%)';
                      break;
                    case 'left':
                      leftStyle = '0';
                      transformStyle = 'none';
                      break;
                    case 'right':
                      leftStyle = '100%';
                      transformStyle = 'translateX(-100%)';
                      break;
                    case 'custom':
                      leftStyle = `${leftPercentCustom}%`;
                      transformStyle = 'none';
                      break;
                  }
                  
                  // アニメーションクラス名を取得
                  const getAnimationClass = () => {
                    if (!animationType || animationType === 'none') return '';
                    return `qwipe-cta-animation-${animationType}`;
                  };
                  
                  return (
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: `${bottomPercent}%`,
                        left: leftStyle,
                        transform: transformStyle,
                        width: `${widthPercent}%`,
                        height: 'auto'
                      }}
                    >
                      <Box
                        className={getAnimationClass()}
                        sx={{
                          borderRadius: '8px',
                          boxSizing: 'border-box',
                          overflow: 'hidden'
                        }}
                      >
                        <img
                          src={imagePreview}
                          alt="プレビュー"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            display: 'block'
                          }}
                        />
                      </Box>
                    </Box>
                  );
                })()}
              </Box>
            </Box>
            
            {/* 右側下部：ボタンエリア */}
            <Box 
              sx={{ 
                display: 'flex', 
                justifyContent: 'flex-end', 
                alignItems: 'center',
                gap: 2,
                p: 2,
                backgroundColor: '#F7F7F7',
              }}
            >
              {/* 削除ボタン（編集時のみ表示） */}
              {ctaButton && onDelete && (
                <IconButton
                  onClick={() => {
                    setShowDeleteDialog(true);
                  }}
                  sx={{
                    backgroundColor: '#FFFFFF',
                    border: '1px dashed #F44336',
                    width: 48,
                    height: 48,
                    padding: 0,
                    borderRadius: '8px',
                    '&:hover': {
                      backgroundColor: '#FFFFFF',
                    },
                  }}
                >
                  <img 
                    src="/assets/images/trash.svg" 
                    alt="削除" 
                    style={{ width: '20px', height: '20px', filter: 'none' }}
                  />
                </IconButton>
              )}
              
              {/* 編集中止ボタン */}
              <Button 
                onClick={handleCloseRequest}
                variant="contained"
                sx={{
                  minWidth: '120px',
                  height: '48px',
                  borderRadius: '8px',
                  backgroundColor: '#9E9E9E',
                  color: '#FFFFFF',
                  '&:hover': {
                    backgroundColor: '#757575',
                  },
                }}
              >
                編集中止
              </Button>
              
              {/* 保存ボタン */}
              <Button 
                onClick={handleSave}
                variant="contained"
                startIcon={<CheckIcon />}
                sx={{
                  minWidth: '120px',
                  height: '48px',
                  borderRadius: '8px',
                  backgroundColor: '#F49121',
                  color: '#FFFFFF',
                  '&:hover': {
                    backgroundColor: '#E0821A',
                  },
                }}
              >
                保存
              </Button>
            </Box>
          </Box>
          </Box>
        </Box>
      </RightDrawer>

      {/* 削除確認ダイアログ */}
      <ConfirmDialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={() => {
          if (ctaButton && onDelete) {
            if (siteId === 'create') {
              onPendingCtaImageFile?.(ctaButton.id, null);
              onPendingCtaHoverImageFile?.(ctaButton.id, null);
            }
            onDelete(ctaButton.id);
            onClose();
          }
          setShowDeleteDialog(false);
        }}
        message="削除します。よろしいですか？"
        confirmText="削除"
        confirmColor="#DB2323"
        confirmHoverColor="#C01F1F"
      />

      {/* 編集中止確認ダイアログ */}
      <ConfirmDialog
        open={confirmCloseOpen}
        onClose={handleCloseCancel}
        onConfirm={handleCloseConfirm}
        message={
          <>
            編集を中止します。よろしいですか?
            <br />
            <Typography sx={{ color: '#F44336', fontSize: '12px', mt: 1 }}>
              ※編集中のデータは保存されません
            </Typography>
          </>
        }
        confirmText="編集中止"
        confirmColor="#9E9E9E"
        confirmHoverColor="#757575"
        reverseButtons={false}
      />
    </>
  );
};

